import { useState } from "react";
import type { Entry, LocaleCode } from "../../shared/types";

interface Props {
  mode: "create" | "edit";
  entry: Entry | null;
  defaultLocale: LocaleCode;
  onSave(form: FormData): void | Promise<void>;
  onCancel(): void;
}

export default function CountryModal({ mode, entry, defaultLocale, onSave, onCancel }: Props) {
  const [title, setTitle] = useState<string>(entry?.titles[defaultLocale] ?? "");
  const [capital, setCapital] = useState<string>(entry?.capitals[defaultLocale] ?? "");
  const [idOverride, setIdOverride] = useState<string>("");
  const [flagFile, setFlagFile] = useState<File | null>(null);
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [emblemFile, setEmblemFile] = useState<File | null>(null);

  function buildForm(): FormData {
    const fd = new FormData();
    if (mode === "create" && idOverride.trim()) fd.append("id", idOverride.trim());
    fd.append(`title_${defaultLocale}`, title);
    fd.append(`capital_${defaultLocale}`, capital);
    if (flagFile) fd.append("flag", flagFile);
    if (mapFile) fd.append("map", mapFile);
    if (emblemFile) fd.append("emblem", emblemFile);
    return fd;
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30">
      <div className="bg-gh-surface rounded-lg shadow-lg max-w-2xl w-full p-6 border border-gh-border max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {mode === "create" ? "New country" : `Edit: ${entry?.id}`}
        </h3>

        {mode === "create" && (
          <div className="mb-4">
            <label className="block text-xs text-gh-muted mb-1">id (optional override)</label>
            <input
              type="text"
              className="w-full px-3 py-1.5 rounded border border-gh-border font-mono text-sm"
              value={idOverride}
              onChange={(e) => setIdOverride(e.target.value)}
              placeholder="auto from EN title"
            />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <FilePicker label="flag (PNG)" onFile={setFlagFile} required={mode === "create"} />
          <FilePicker label="map (PNG)" onFile={setMapFile} required={mode === "create"} />
          <FilePicker label="emblem (PNG)" onFile={setEmblemFile} required={mode === "create"} />
        </div>

        <div className="grid grid-cols-1 gap-3 mb-4">
          <div>
            <label className="block text-xs text-gh-muted mb-1">title ({defaultLocale})</label>
            <input
              type="text"
              className="w-full px-3 py-1.5 rounded border border-gh-border text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gh-muted mb-1">capital ({defaultLocale})</label>
            <input
              type="text"
              className="w-full px-3 py-1.5 rounded border border-gh-border text-sm"
              value={capital}
              onChange={(e) => setCapital(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 rounded border border-gh-border text-sm hover:bg-gh-bg"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(buildForm())}
            className="px-4 py-1.5 rounded bg-gh-accent text-white text-sm hover:opacity-90"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function FilePicker({
  label,
  onFile,
  required,
}: {
  label: string;
  onFile(f: File | null): void;
  required: boolean;
}) {
  return (
    <label className="text-xs text-gh-muted block">
      {label}
      {required ? " *" : ""}
      <input
        type="file"
        accept="image/png"
        className="block mt-1 w-full text-xs"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}
